import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { CalculationOutput } from "components/ui/display/CalculationOutput"
import { DropdownMenu, DropdownOption } from "components/ui/inputs/DropdownMenu"
import styled, { useTheme } from "styled-components"
import { Input } from "components/ui/inputs/Input";
import { useErrors } from "providers/ErrorsProvider";
import { useEffect, useMemo, useState } from "react";
import { calculationLimits } from "store/calculation.config";
import { SwitchRow } from "components/ui/controls/SwitchRow";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { generateTicks } from "utilities/generateTicks";
import { calculateAcceleration } from "./Acceleration.calculate";

import { ReactComponent as RightStraightCrossing } from 'assets/img/acceleration_right_straight.svg'
import { ReactComponent as RightRightCrossing } from 'assets/img/acceleration_right_right.svg'
import { ReactComponent as RightLeftCrossing } from 'assets/img/acceleration_right_left.svg'
import { ReactComponent as StraightLeftCrossing } from 'assets/img/acceleration_straight_left.svg'
import { ReactComponent as LeftLeftCrossing } from 'assets/img/acceleration_left_left.svg'
import { ContentContainer } from "components/ui/display/ContentContainer";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const Acceleration = () => {

    const GlobalTheme = useTheme();
    const {setError, unsetError} = useErrors();
    const [rawInput, setRawInput] = useState({
        speedlimit: '',
        slope: '',
        mainRoadRadius: '',
        rampRadius: '',
        rampCrossSlope: '',
        rampConnectionCrossSlope: '',
        mainRoadCrossSlope: '',
        designVehicle: ''
    });

    const [calculationInput, setCalculationInput] = useState({
        strictMode: false,
        designClass: '',
        crossingType: '',
        speedlimit: '',
        slope: '',
        mainRoadRadius: '',
        rampRadius: '',
        rampCrossSlope: '',
        rampConnectionCrossSlope: '',
        mainRoadCrossSlope: '',
        designVehicle: 'personbil'
    });

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateAcceleration(calculationInput);

        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }

        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });

    }, [calculationInput, setError, unsetError]);
    
    const XAxisTicks = useMemo(() => {
        if(!calculatedState.output?.accelerationData?.valid) return null;

        return generateTicks({
            min: Math.round(calculatedState.output.accelerationData.value[0].distance),
            max: Math.round(calculatedState.output.accelerationData.value[calculatedState.output.accelerationData.value.length-1].distance),
            targetTicks: 10
        });
    }, [calculatedState]);

    const YAxisTicks = useMemo(() => {
        if(!calculatedState.output?.accelerationData?.valid) return null;

        const min = Math.min(...calculatedState.output.accelerationData.value.map(c => c.speed));
        const max = Math.max(...calculatedState.output.accelerationData.value.map(c => c.speed));

        return generateTicks({
            min: min,
            max: max,
            step: 10,
            expand: true
        });
    }, [calculatedState]);

    return (
        <StyledAcceleration>
            <ToolsColumn className='inputColumn'>
                <ToolsContainer title="Inndata">
                    <SwitchRow
                        icon={<MaterialIcon name='contract' weight={300}/>}
                        title={"Bruk konsekvent modus"}
                        isChecked={calculationInput.strictMode}
                        onChange={(e) => setCalculationInput(prev => ({...prev, strictMode: e.target.checked}))}
                    />
                    {
                        calculationInput.strictMode ? 
                        <DropdownMenu
                            label="Dimensjoneringsklasse"
                            defaultValue={null}
                            defaultIcon={<MaterialIcon name='road' weight={300}/>}
                            defaultName='Velg dimensjoneringsklasse'
                            defaultSelected={calculationInput.designClass}
                            onSelect={(e) => setCalculationInput(prev => ({...prev, designClass: e}))}
                        >
                            <DropdownOption value='h1-80' name='H1 (80km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h1-90' name='H1 (90km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h2-90' name='H2 (90km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h3-90' name='H3 (90km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h3-100' name='H3 (100km/t) - Nasjonal hovedveg'/>
                            <DropdownOption value='h3-110' name='H3 (110km/t) - Nasjonal hovedveg'/>
                        </DropdownMenu>
                        : null
                    }
                    <DropdownMenu
                        label="Kurvekombinasjon"
                        defaultValue={null}
                        defaultIcon={<MaterialIcon name='flyover' weight={300}/>}
                        defaultName='Velg kurvekombinasjon...'
                        onSelect={(e) => {setCalculationInput(prev => ({...prev, crossingType: e}))}}
                    >
                        <DropdownOption value='right-straight' name='Høyre - Rett'/>
                        <DropdownOption value='right-right' name='Høyre - Høyre'/>
                        <DropdownOption value='right-left' name='Høyre - Venstre'/>
                        <DropdownOption value='straight-left' name='Rett - Venstre'/>
                        <DropdownOption value='left-left' name='Venstre - Venstre'/>
                    </DropdownMenu>
                    <DropdownMenu
                        label="Dimensjonerende kjøretøy"
                        defaultValue={null}
                        defaultIcon={<MaterialIcon name='traffic_jam' weight={300}/>}
                        defaultName='Velg dim. kjøretøy...'
                        defaultSelected={calculationInput.designVehicle}
                        onSelect={(e) => {setCalculationInput(prev => ({...prev, designVehicle: e}))}}
                    >
                        <DropdownOption value='personbil' name='Personbil'/>
                        <DropdownOption value='buss' name='Buss'/>
                        <DropdownOption value='vogntog' name='Vogntog'/>
                    </DropdownMenu>
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error' ?? null}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning' ?? null}
                        value={rawInput.speedlimit}
                        onChange={(e) => setRawInput(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, speedlimit: e.target.value}))}
                    />
                    <Input
                        title="Stigning på hovedvegen"
                        prefix={<span>s</span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.slope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.slope?.severity === 'warning' ?? null}
                        value={rawInput.slope}
                        onChange={(e) => setRawInput(prev => ({...prev, slope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, slope: e.target.value}))}
                    />
                    
                    <Input
                        title="Radius på hovedvegen"
                        helptext="Positiv fortegn indikerer høyrekurve, negativ fortegn indikerer venstrekurve."
                        prefix={<span>R<sub>h,1</sub></span>}
                        sufix='m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.mainRoadRadius?.severity === 'error' ?? null}
                        warning={calculatedState.input?.mainRoadRadius?.severity === 'warning' ?? null}
                        value={calculationInput.crossingType === 'right-straight' ? '' : rawInput.mainRoadRadius}
                        onChange={(e) => setRawInput(prev => ({...prev, mainRoadRadius: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, mainRoadRadius: e.target.value}))}
                        disabled={calculationInput.crossingType === 'right-straight'}
                    />

                    <Input
                        title="Radius på rampen"
                        prefix={<span>R<sub>h,2</sub></span>}
                        sufix='m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.rampRadius?.severity === 'error' ?? null}
                        warning={calculatedState.input?.rampRadius?.severity === 'warning' ?? null}
                        value={calculationInput.crossingType === 'straight-left' ? '' : rawInput.rampRadius}
                        onChange={(e) => setRawInput(prev => ({...prev, rampRadius: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, rampRadius: e.target.value}))}
                        disabled={calculationInput.crossingType === 'straight-left'}
                    />
                    
                    <Input
                        title="Tverrfall på rampen"
                        prefix={<span>e<sub>1</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minCrossSlope}
                        max={calculationLimits.maxCrossSlope}
                        step={0.01}
                        error={calculatedState.input?.rampCrossSlope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.rampCrossSlope?.severity === 'warning' ?? null}
                        value={rawInput.rampCrossSlope}
                        onChange={(e) => setRawInput(prev => ({...prev, rampCrossSlope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, rampCrossSlope: e.target.value}))}
                    />
                    <Input
                        title="Tverrfall på rampetilslutning"
                        prefix={<span>e<sub>x</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minCrossSlope}
                        max={calculationLimits.maxCrossSlope}
                        step={0.01}
                        error={calculatedState.input?.rampConnectionCrossSlope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.rampConnectionCrossSlope?.severity === 'warning' ?? null}
                        value={rawInput.rampConnectionCrossSlope}
                        onChange={(e) => setRawInput(prev => ({...prev, rampConnectionCrossSlope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, rampConnectionCrossSlope: e.target.value}))}
                    />
                    <Input
                        title="Tverrfall på hovedvegen"
                        prefix={<span>e<sub>2</sub></span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minCrossSlope}
                        max={calculationLimits.maxCrossSlope}
                        step={0.01}
                        error={calculatedState.input?.mainRoadCrossSlope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.mainRoadCrossSlope?.severity === 'warning' ?? null}
                        value={rawInput.mainRoadCrossSlope}
                        onChange={(e) => setRawInput(prev => ({...prev, mainRoadCrossSlope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, mainRoadCrossSlope: e.target.value}))}
                    />
                                        
                </ToolsContainer>
                <ToolsContainer title="Faste variabler">
                    <Input
                        title="Startfart"
                        prefix={<span>v<sub>0</sub></span>}
                        sufix='km/t'
                        type='number'
                        value={calculatedState.derived?.initialSpeed?.value?.toFixed(0) ?? ''}
                        disabled
                    />
                    <Input
                        title="Hjulavstand"
                        prefix={<span>b</span>}
                        sufix='m'
                        type='number'
                        value={calculatedState.derived?.wheelbase?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                    <Input
                        title="Relativ vertikalfart"
                        prefix={<span>v<sub>vf</sub></span>}
                        sufix='m/m'
                        type='number'
                        value={calculatedState.derived?.relativeVerticalSpeed?.value?.toFixed(3) ?? ''}
                        disabled
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className='outputColumn'>
                <CalculationOutput
                    title="Lengde på akselerasjonsfelt"
                    prefix={<span>L<sub>1</sub></span>}
                    value={calculatedState.output?.accelerationLength?.value?.toFixed(0) ?? '--'}
                    error={calculatedState.output?.accelerationLength?.severity === 'error'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på overgangsstrekning"
                    prefix={<span>L<sub>2</sub></span>}
                    value={calculatedState.output?.transitionLength?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på overhøydeoppbygging"
                    prefix={<span>L<sub>0,min</sub></span>}
                    value={calculatedState.output?.curvedRunoffLength?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på overhøydeutjevning"
                    prefix={<span>L<sub>x,min</sub></span>}
                    value={calculatedState.output?.tangentialRunoffLength?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Klotoideparameter"
                    prefix={<span>A<sub>min</sub></span>}
                    value={calculatedState.output?.clothoideParameter?.value?.toFixed(0) ?? '--'}
                    sufix='m'
                />
            </ToolsColumn>
            <ToolsColumn className='infoColumn'>
                {
                    calculatedState.input?.crossingType?.valid ?
                    <ContentContainer padding='lg' className='crossingTypeContainer'>
                        {
                            calculatedState.input.crossingType.value === 'right-straight' ? <RightStraightCrossing/> :
                            calculatedState.input.crossingType.value === 'right-right' ? <RightRightCrossing/> :
                            calculatedState.input.crossingType.value === 'right-left' ? <RightLeftCrossing/> :
                            calculatedState.input.crossingType.value === 'straight-left' ? <StraightLeftCrossing/> :
                            calculatedState.input.crossingType.value === 'left-left' ? <LeftLeftCrossing/> : null
                        }
                    </ContentContainer>
                    : null
                }
                
                {
                    calculatedState.output?.accelerationData?.valid ?
                    <ToolsContainer
                        title="Fart som funksjon av tilbakelagt avstand"
                    >
                        <div className="graphContainer">
                            <ResponsiveContainer
                                height='100%'
                                width='100%'
                                minWidth='400px'
                                minHeight='0'
                                aspect={undefined}
                            >
                                <LineChart
                                    margin={{top: 10, left: 30, right: 30, bottom: 0}}
                                    //data={calculatedState.output.accelerationData.value}
                                >
                                    <Legend verticalAlign="bottom" layout="vertical"/>
                                    <XAxis
                                        type="number"
                                        dataKey="distance"
                                        height={50}
                                        domain={[0, 'dataMax']}
                                        ticks={XAxisTicks}
                                        //tickFormatter={(d) => { return Math.round(d)}}
                                        label={{
                                            value: 'Avstand [m]',
                                            position: 'center',
                                            dy: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                        fontSize={12}
                                    />
                                    <YAxis
                                        domain={[
                                            YAxisTicks[0],
                                            YAxisTicks[YAxisTicks.length-1]
                                        ]}
                                        ticks={YAxisTicks}
                                        dataKey={'speed'}
                                        label={{
                                            value: 'Hastighet [km/t]',
                                            angle: -90,
                                            position: 'left',
                                            dx: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                        fontSize={12}
                                    />
                                    <CartesianGrid
                                        strokeDasharray={'3 3'}
                                    />
                                    <Line
                                        dataKey={'speed'}
                                        name='Fart ikke OK. Tid ikke OK.'
                                        dot={false}
                                        data={calculatedState.output.accelerationData.value.filter(d => d.segment === 1)}
                                        stroke={GlobalTheme.colors.errorForeground}
                                        strokeWidth={3}
                                    />
                                    {
                                        calculatedState.output.accelerationData.value.filter(d => d.segment === 2).length > 0 ?
                                        <Line
                                            dataKey={'speed'}
                                            name='Fart OK. Tid ikke OK.'
                                            dot={false}
                                            data={calculatedState.output.accelerationData.value.filter(d => d.segment === 2)}
                                            stroke={GlobalTheme.colors.warningForeground}
                                            strokeWidth={3}
                                        />
                                        : null
                                    }
                                    
                                    <Line
                                        dataKey={'speed'}
                                        name='Fart OK. Tid OK.'
                                        dot={false}
                                        data={calculatedState.output.accelerationData.value.filter(d => d.segment === 3)}
                                        stroke={GlobalTheme.colors.successForeground}
                                        strokeWidth={3}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ToolsContainer>
                    : null
                }
                    
            </ToolsColumn>
        </StyledAcceleration>
    )
}

const StyledAcceleration = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }

    div.graphContainer
    {
        min-height: 300px;
        overflow: auto;
    }

    div.crossingTypeContainer
    {
        //background: ${({theme}) => theme.colors.neutralPrimaryBorder};

        svg
        {
            width: 100%;
            height: auto;
        }
    }
`